/**
 * One real feature, for the pop-up panel's live preview.
 *
 * The preview is only worth having if it shows the values an end user will see,
 * so this asks the service for a single record rather than inventing sample
 * text. Never throws: a service that will not answer should leave the panel
 * fully usable with an empty preview, not break it.
 *
 * Field DISCOVERY is not repeated here — the labelling service already does
 * that for all three sources, and the panel reuses it.
 */

import type { AppModule, Layer } from '../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';

/** WFS GetFeature URL for one record of a GeoServer layer. */
function getWfsSampleUrl(geoServerUrl: string, layerName: string): string {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path)
      ? path.replace(/\/(wms|wfs)$/i, '/wfs')
      : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('typeNames', layerName);
    url.searchParams.set('outputFormat', 'application/json');
    url.searchParams.set('count', '1');
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * The attributes of one feature from this layer, or an empty object.
 *
 * The shape matches what the map's identify path hands the formatter, so the
 * preview and the real pop-up are fed the same kind of value.
 */
export async function fetchSampleAttributes(
  layer: Layer,
  module: AppModule,
): Promise<Record<string, unknown>> {
  try {
    if (layer.localData) {
      return (layer.localData.features ?? [])[0]?.properties ?? {};
    }

    if (layer.arcgisLayerId !== undefined) {
      const effectiveUrl = arcgisServiceUrlFor(layer, module);
      const baseUrl = getArcgisBaseUrl(effectiveUrl);
      if (!baseUrl) return {};

      const params = new URLSearchParams({
        where: '1=1',
        outFields: '*',
        returnGeometry: 'false',
        resultRecordCount: '1',
        f: 'json',
      });
      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl),
      );
      if (!response.ok) return {};
      const data = await response.json();
      if (data?.error) return {};
      return data?.features?.[0]?.attributes ?? {};
    }

    if (!module.geoServerUrl) return {};
    const url = getWfsSampleUrl(module.geoServerUrl, layer.name);
    if (!url) return {};

    const response = await fetch(url);
    if (!response.ok) return {};
    const data = await response.json();
    return data?.features?.[0]?.properties ?? {};
  } catch (error) {
    console.error(`[popup] sample feature failed layer="${layer.name}"`, error);
    return {};
  }
}
